/* Vertical drag handle that sits between two horizontally-arranged panes.
 * Emits a `delta` (mouse-x change since last move) on every mousemove
 * during a drag. The parent decides which pane to grow/shrink and by
 * how much — this component is layout-agnostic. */

import { useRef } from "react";

export function PaneResizer({
  id,
  onResize,
  onResizeEnd,
}: {
  /** Stable id for the dragged divider — used for the DOM id only. */
  id: string;
  /** Called per mousemove during drag with delta-x in pixels. */
  onResize: (deltaX: number) => void;
  /** Called once on mouseup so the parent can persist the final width. */
  onResizeEnd?: () => void;
}) {
  const lastX = useRef(0);
  const dragging = useRef(false);

  function onMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    if (dragging.current) return;
    dragging.current = true;
    lastX.current = e.clientX;

    function onMove(ev: MouseEvent) {
      const delta = ev.clientX - lastX.current;
      lastX.current = ev.clientX;
      if (delta !== 0) onResize(delta);
    }
    function onUp() {
      dragging.current = false;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      document.body.style.removeProperty("cursor");
      document.body.style.removeProperty("user-select");
      onResizeEnd?.();
    }

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    document.body.style.cursor = "col-resize";
    // Prevent text selection during drag — without this, dragging across
    // the canvas highlights every paragraph the cursor passes over.
    document.body.style.userSelect = "none";
  }

  return (
    <div
      id={`agent-desk-resizer-${id}`}
      className="agent-desk-resizer"
      onMouseDown={onMouseDown}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize pane"
    />
  );
}
